import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVertOutlined';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import { Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { Fragment, useId, useState } from 'react';

import { actionIconOf } from './actionIcon';
import type { OutstandingAction } from './rowActions';

interface RowActionsMenuProps {
  /** The row's commands — the things that change something. */
  actions: OutstandingAction[];
  /** The row's paperwork, each offering View and Download. Below the commands,
   *  because a document tells you about the row rather than acting on it. */
  documents?: OutstandingAction[];
  /** The row this menu belongs to, so the trigger's label names it rather than
   *  saying "More actions" thirty times over. */
  itemLabel: string;
  onSelect: (action: OutstandingAction) => void;
  /** Opens one document in the row's viewer. */
  onView?: (action: OutstandingAction) => void;
}

/**
 * The overflow menu for everything that is not the row's primary action: its
 * remaining commands, then its paperwork.
 *
 * One menu, not two. The documents were briefly listed as rows on the card
 * itself, each with its own kebab, which put a column of identical controls
 * down the middle of the row competing with the single button the row exists
 * for. They belong behind the same control as the commands — one trigger at the
 * end of the row, beside Sign or Pay.
 *
 * Each document offers both routes, because they answer different questions:
 * View is "what am I about to sign", Download is "I need this file". A document
 * with no file stays as one disabled item rather than being dropped — these are
 * known slots, and one that silently vanishes leaves a director unsure whether
 * it is missing or whether they misremembered it existing.
 */
export const RowActionsMenu = ({
  actions,
  documents = [],
  itemLabel,
  onSelect,
  onView,
}: RowActionsMenuProps) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuId = useId();

  if (actions.length === 0 && documents.length === 0) return null;

  const close = () => {
    setAnchor(null);
  };

  return (
    <>
      <IconButton
        aria-label={`More actions for ${itemLabel}`}
        aria-controls={anchor === null ? undefined : menuId}
        aria-haspopup="true"
        aria-expanded={anchor === null ? undefined : true}
        onClick={(event) => {
          setAnchor(event.currentTarget);
        }}
      >
        <MoreVertIcon />
      </IconButton>

      <Menu
        id={menuId}
        anchorEl={anchor}
        open={anchor !== null}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {actions.map((action) => (
          <MenuItem
            key={`${action.kind}-${action.label}`}
            onClick={() => {
              onSelect(action);
              close();
            }}
          >
            <ListItemIcon>{actionIconOf(action.kind)}</ListItemIcon>
            <ListItemText primary={action.label} />
          </MenuItem>
        ))}

        {documents.map((document, index) => (
          <Fragment key={`${document.kind}-${document.label}`}>
            {/* Above the first document and between each pair after it, so the
                menu reads as commands, then one group per file, rather than one
                undifferentiated list. */}
            {(index > 0 || actions.length > 0) && <Divider />}

            {document.href === undefined || document.href === '' ? (
              <MenuItem disabled>
                <ListItemIcon>
                  <VisibilityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={`View ${document.label}`} />
              </MenuItem>
            ) : (
              [
                <MenuItem
                  key="view"
                  onClick={() => {
                    onView?.(document);
                    close();
                  }}
                >
                  <ListItemIcon>
                    <VisibilityIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={`View ${document.label}`} />
                </MenuItem>,

                // An anchor, not a handler: middle-click and "open in new tab"
                // are how some directors save a file, and a MenuItem running
                // onClick would honour neither.
                <MenuItem
                  key="download"
                  component="a"
                  href={document.href}
                  target="_blank"
                  rel="noreferrer"
                  onClick={close}
                >
                  <ListItemIcon>
                    <DownloadIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={`Download ${document.label}`} />
                </MenuItem>,
              ]
            )}
          </Fragment>
        ))}
      </Menu>
    </>
  );
};
